import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { StatusPill } from "@/components/PortalShell";
import { X, UploadCloud, Loader2, FileText } from "lucide-react";
import ReportForm from "@/components/ReportForm";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export const Card = ({ children, ...p }) => <div className="card-soft" style={{ padding: 22 }} {...p}>{children}</div>;

export function AddReport({ pid, reports, reload }) {
  return (
    <div style={{ display: "grid", gap: 22 }}>
      <ReportForm pid={pid} reload={reload} />
      <div style={{ display: "grid", gap: 12 }}>
        {reports.map((r) => <Card key={r.report_id}><div style={{ display: "flex", justifyContent: "space-between" }}><b>{r.title}</b><StatusPill status={r.status} /></div><p style={{ color: "var(--muted)", fontSize: 14, marginTop: 6 }}>{r.summary}</p></Card>)}
      </div>
    </div>
  );
}

export function DocsTab({ pid, documents, reload }) {
  const [category, setCategory] = useState("Contracts");
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);
  const inputRef = React.useRef(null);
  const CATS = ["General", "Plans", "Insurance", "Contracts", "Warranties", "Manuals"];

  const uploadFiles = async (files) => {
    if (!pid) return toast.error("Select a property");
    const arr = Array.from(files).filter((f) => f.type.startsWith("image/") || f.type === "application/pdf");
    if (!arr.length) return toast.error("Only PDF or image files are allowed");
    setBusy(true);
    let n = 0;
    for (const file of arr) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
        const title = file.name.replace(/\.[^.]+$/, "");
        await api.post("/documents", { property_id: pid, title, category, url: `${BACKEND}${data.url}` });
        n++;
      } catch (e) { toast.error(apiErr(e)); }
    }
    setBusy(false);
    if (n) { toast.success(`${n} document${n === 1 ? "" : "s"} added`); reload(); }
  };
  const onDrop = (e) => { e.preventDefault(); setDrag(false); if (e.dataTransfer.files?.length) uploadFiles(e.dataTransfer.files); };

  return (
    <div style={{ display: "grid", gap: 20 }}>
      <div className="card-soft" style={{ padding: 22, display: "grid", gap: 14, maxWidth: 620 }}>
        <div className="lbl">Upload documents</div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <span className="lbl-m">File the upload under</span>
          <select className="inp" style={{ maxWidth: 200 }} value={category} onChange={(e) => setCategory(e.target.value)} data-testid="doc-category-select">{CATS.map((c) => <option key={c}>{c}</option>)}</select>
        </div>
        <div
          data-testid="doc-dropzone"
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={onDrop}
          style={{ border: `1.5px dashed ${drag ? "var(--honey)" : "var(--line)"}`, background: drag ? "rgba(176,141,87,.06)" : "var(--soft)", borderRadius: 6, padding: "30px 18px", textAlign: "center", cursor: "pointer", transition: "all .2s", color: "var(--muted)" }}>
          {busy ? (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 10 }}><Loader2 size={18} className="spin" /> Uploading…</span>
          ) : (
            <span style={{ display: "inline-flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
              <UploadCloud size={26} strokeWidth={1.4} />
              <span style={{ fontSize: 14 }}>Tap to choose contracts or PDFs from your device</span>
              <span style={{ fontSize: 12 }}>or drag & drop files here (PDF or image)</span>
            </span>
          )}
          <input ref={inputRef} type="file" accept="application/pdf,image/*" multiple onChange={(e) => uploadFiles(e.target.files)} style={{ display: "none" }} data-testid="doc-input" />
        </div>
      </div>
      <div style={{ display: "grid", gap: 10 }} data-testid="doc-list">
        {documents.length === 0
          ? <p style={{ fontSize: 13, color: "var(--muted)" }}>No documents yet. Upload contracts, plans or manuals above.</p>
          : documents.map((d) => (
            <Card key={d.document_id}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                  <FileText size={18} strokeWidth={1.5} style={{ flexShrink: 0, color: "var(--umber)" }} />
                  <div style={{ minWidth: 0 }}><b style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{d.title}</b><span className="lbl-m">{d.category}</span></div>
                </div>
                {d.url && <a href={d.url} target="_blank" rel="noreferrer" className="link-u" style={{ flexShrink: 0 }} data-testid="doc-open-link">Open</a>}
              </div>
            </Card>
          ))}
      </div>
      <style>{`.spin{animation:spin 1s linear infinite}@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

export function InvoicesTab({ pid, invoices, reload, autofill, onAutofilled }) {
  const thisMonth = new Date().toISOString().slice(0, 7);
  const [f, setF] = useState({ month: thisMonth, amount: "", due_date: "", send: true });
  const [adds, setAdds] = useState([]);
  const [eventIds, setEventIds] = useState([]);
  const [jobIds, setJobIds] = useState([]);
  const [preview, setPreview] = useState(false);
  const [busy, setBusy] = useState(false);
  const [discountPct, setDiscountPct] = useState(0);
  const buildDraft = useCallback(async (silent, month) => {
    if (!pid) return;
    try {
      const { data } = await api.get(`/invoices/draft?property_id=${pid}${month ? `&month=${month}` : ""}`);
      setF((s) => ({ ...s, month: data.month || s.month, amount: data.base_amount || "" }));
      setAdds((data.additions || []).map((a) => ({ label: a.label, amount: a.amount })));
      setEventIds(data.event_ids || []);
      setJobIds(data.job_ids || []);
      setDiscountPct(data.discount_pct || 0);
      if (!silent) { const n = (data.additions || []).length; toast.success(`Synced ${data.month_label || ""} · plan fee + ${n} unbilled extra${n === 1 ? "" : "s"}`); }
    } catch (e) { if (!silent) toast.error(apiErr(e)); }
  }, [pid]);
  useEffect(() => { buildDraft(true); if (autofill && onAutofilled) onAutofilled(); }, [buildDraft]); // eslint-disable-line
  const addLine = () => setAdds([...adds, { label: "", amount: "" }]);
  const updLine = (i, k, v) => setAdds(adds.map((a, idx) => idx === i ? { ...a, [k]: v } : a));
  const rmLine = (i) => setAdds(adds.filter((_, idx) => idx !== i));
  const base = parseFloat(f.amount) || 0;
  const extra = adds.reduce((s, a) => s + (parseFloat(a.amount) || 0), 0);
  const subtotal = base + extra;
  const discPct = parseFloat(discountPct) || 0;
  const discAmt = subtotal * discPct / 100;
  const total = (subtotal - discAmt).toFixed(2);
  const cleanAdds = adds.filter((a) => a.label && a.amount).map((a) => ({ label: a.label, amount: parseFloat(a.amount) }));
  const openPreview = (e) => { e.preventDefault(); if (!pid) return toast.error("Select a property"); if (base <= 0 && cleanAdds.length === 0) return toast.error("Add a monthly fee or a line item"); setPreview(true); };
  const doSend = async () => {
    setBusy(true);
    try {
      await api.post("/invoices", { property_id: pid, month: f.month, amount: base, additions: cleanAdds, due_date: f.due_date, send: f.send, event_ids: eventIds, job_ids: jobIds, discount_pct: discPct });
      toast.success(f.send ? "Invoice created & emailed to client" : "Invoice created");
      setF({ month: thisMonth, amount: "", due_date: "", send: true }); setAdds([]); setEventIds([]); setJobIds([]); setDiscountPct(0); setPreview(false); reload();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const monthLabel = (() => { try { return new Date(`${f.month}-01`).toLocaleDateString("en-GB", { month: "long", year: "numeric" }); } catch { return f.month; } })();
  const openBlob = async (fn) => { const w = window.open("", "_blank"); try { const res = await fn(); const url = URL.createObjectURL(res.data); if (w) w.location.href = url; else window.location.href = url; } catch (e) { if (w) w.close(); toast.error("Could not open PDF"); } };
  const viewPreviewPdf = () => openBlob(() => api.post("/invoices/preview-pdf", { property_id: pid, month: f.month, amount: base, additions: cleanAdds, discount_pct: discPct, due_date: f.due_date, send: false, event_ids: [], job_ids: [] }, { responseType: "blob" }));
  return (
    <div style={{ display: "grid", gap: 20 }}>
      <form onSubmit={openPreview} className="card-soft" style={{ padding: 22, display: "grid", gap: 14, maxWidth: 620 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
          <div className="lbl">Raise a monthly invoice</div>
          <button type="button" className="btn-outline" style={{ padding: "6px 14px", fontSize: 13 }} onClick={() => buildDraft(false, f.month)} data-testid="build-invoice-btn">Re-sync from plan</button>
        </div>
        <div style={{ fontSize: 12, color: "var(--muted)", marginTop: -4 }}>The selected month's plan fee and that month's unbilled visit costs are filled in automatically. Change the month to re-sync. You'll always preview before it sends.</div>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          <div style={{ flex: 1, minWidth: 160 }}><div className="lbl-m" style={{ marginBottom: 4 }}>Month</div><input className="inp" type="month" value={f.month} onChange={(e) => { const m = e.target.value; setF((s) => ({ ...s, month: m })); if (m) buildDraft(true, m); }} required data-testid="invoice-month-input" /></div>
          <div style={{ flex: 1, minWidth: 160 }}><div className="lbl-m" style={{ marginBottom: 4 }}>Monthly fee (€)</div><input className="inp" type="number" step="0.01" placeholder="0.00" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} required data-testid="invoice-amount-input" /></div>
        </div>
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div className="lbl-m">Additions</div>
            <button type="button" className="link-u" onClick={addLine} data-testid="add-line-btn">+ Add item</button>
          </div>
          <div style={{ display: "grid", gap: 8, marginTop: 8 }}>
            {adds.map((a, i) => (
              <div key={i} style={{ display: "flex", gap: 8 }}>
                <input className="inp" placeholder="e.g. Pool repair, extra cleaning" value={a.label} onChange={(e) => updLine(i, "label", e.target.value)} data-testid={`line-label-${i}`} />
                <input className="inp" style={{ maxWidth: 130 }} type="number" step="0.01" placeholder="€" value={a.amount} onChange={(e) => updLine(i, "amount", e.target.value)} data-testid={`line-amount-${i}`} />
                <button type="button" className="cal-nav" onClick={() => rmLine(i)} aria-label="Remove item"><X size={16} /></button>
              </div>
            ))}
          </div>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderTop: "1px solid var(--line)", paddingTop: 12 }}>
          <span className="lbl-m">Total</span>
          <span className="font-serif-c" style={{ fontSize: 26 }} data-testid="invoice-total">€{total}</span>
        </div>
        <div style={{ display: "flex", gap: 16, alignItems: "flex-end", flexWrap: "wrap" }}>
          <div><div className="lbl-m" style={{ marginBottom: 4 }}>Discount %</div><input className="inp" type="number" min="0" max="100" style={{ maxWidth: 100 }} value={discountPct} onChange={(e) => setDiscountPct(e.target.value)} data-testid="invoice-discount" placeholder="0" /></div>
          <div><div className="lbl-m" style={{ marginBottom: 4 }}>Due date (optional)</div><input className="inp" type="date" value={f.due_date} onChange={(e) => setF({ ...f, due_date: e.target.value })} /></div>
          <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 14, paddingBottom: 12 }}><input type="checkbox" checked={f.send} onChange={(e) => setF({ ...f, send: e.target.checked })} data-testid="invoice-send-toggle" /> Email to client</label>
        </div>
        <button className="btn-ink" type="submit" style={{ justifySelf: "start" }} data-testid="preview-invoice-btn">Preview invoice</button>
      </form>

      {preview && (
        <div onClick={() => setPreview(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.5)", display: "grid", placeItems: "center", zIndex: 1000, padding: 20 }}>
          <div onClick={(e) => e.stopPropagation()} className="card-soft" style={{ padding: 24, maxWidth: 480, width: "100%", background: "var(--paper)", maxHeight: "80vh", overflow: "auto" }} data-testid="single-invoice-preview">
            <div className="lbl">Invoice preview · {monthLabel}</div>
            <div style={{ margin: "14px 0", display: "grid", gap: 8 }}>
              <div style={{ display: "flex", justifyContent: "space-between", borderBottom: "1px solid var(--line)", paddingBottom: 8 }}><span>Property care, {monthLabel}</span><b>€{base.toFixed(2)}</b></div>
              {cleanAdds.map((a, i) => <div key={i} style={{ display: "flex", justifyContent: "space-between", borderBottom: "1px solid var(--line)", paddingBottom: 8 }}><span>{a.label}</span><b>€{a.amount.toFixed(2)}</b></div>)}
              {discAmt > 0 && <div style={{ display: "flex", justifyContent: "space-between", borderBottom: "1px solid var(--line)", paddingBottom: 8, color: "#6b4e8a" }}><span>Discount ({discPct}%)</span><b>-€{discAmt.toFixed(2)}</b></div>}
              <div style={{ display: "flex", justifyContent: "space-between", paddingTop: 4 }}><b>Total</b><b className="font-serif-c" style={{ fontSize: 20 }}>€{total}</b></div>
            </div>
            <p style={{ fontSize: 13, color: "var(--muted)" }}>{f.send ? "This will be emailed to the owner and appear in their portal." : "This will be created without emailing the owner."}</p>
            <div style={{ display: "flex", gap: 10, marginTop: 18, justifyContent: "flex-end", flexWrap: "wrap" }}>
              <button className="btn-outline" onClick={() => setPreview(false)}>Back</button>
              <button className="btn-outline" onClick={viewPreviewPdf} data-testid="view-pdf-btn">View branded PDF</button>
              <button className="btn-ink" disabled={busy} onClick={doSend} data-testid="confirm-invoice-btn">{busy ? "Sending…" : f.send ? "Confirm & send" : "Confirm & create"}</button>
            </div>
          </div>
        </div>
      )}

      <div style={{ display: "grid", gap: 10 }}><div className="lbl-m">Invoice history</div>{invoices.length === 0 && <p style={{ color: "var(--muted)", fontSize: 13 }}>No invoices raised yet.</p>}{invoices.map((inv) => <Card key={inv.invoice_id}><div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}><span>{inv.description}, <b>€{inv.amount}</b>{inv.discount_pct > 0 ? <span style={{ fontSize: 12, color: "#6b4e8a" }}> · {inv.discount_pct}% off</span> : null}</span><span style={{ display: "flex", gap: 10, alignItems: "center" }}><StatusPill status={inv.status} /><a className="link-u" href={`${BACKEND}/api/invoices/${inv.invoice_id}/pdf`} target="_blank" rel="noreferrer" data-testid="invoice-pdf-btn" style={{ color: "var(--honey)", fontSize: 13, textDecoration: "none" }}>PDF</a></span></div>{inv.additions?.length > 0 && <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 6 }}>{inv.additions.map((a, i) => <span key={i}>{a.label} €{a.amount}{i < inv.additions.length - 1 ? " · " : ""}</span>)}</div>}</Card>)}</div>
    </div>
  );
}

export function IssuesTab({ issues, reload }) {
  const [filter, setFilter] = useState("open");
  const update = async (id, body) => { try { await api.put(`/issues/${id}`, body); toast.success("Updated"); reload(); } catch (e) { toast.error(apiErr(e)); } };
  const shown = issues.filter((i) => filter === "all" ? true : filter === "open" ? i.status !== "resolved" : i.status === "resolved");
  const openCount = issues.filter((i) => i.status !== "resolved").length;
  return (
    <div style={{ display: "grid", gap: 14 }}>
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        {["open", "all", "resolved"].map((k) => <button key={k} className={filter === k ? "btn-ink" : "btn-outline"} style={{ padding: "5px 14px", fontSize: 13, textTransform: "capitalize" }} onClick={() => setFilter(k)} data-testid={`issue-filter-${k}`}>{k}{k === "open" ? ` (${openCount})` : ""}</button>)}
      </div>
      {shown.map((i) => <IssueRow key={i.issue_id} i={i} update={update} />)}
      {shown.length === 0 && <p style={{ color: "var(--muted)" }}>No {filter === "all" ? "" : filter + " "}issues for this property.</p>}
    </div>
  );
}

function IssueRow({ i, update }) {
  const [note, setNote] = useState(i.staff_note || "");
  const [quote, setQuote] = useState(i.quote_amount || "");
  const fmtD = (d) => { const dt = new Date(d); return isNaN(dt.getTime()) ? "" : dt.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "2-digit" }); };
  return (
    <Card data-testid="staff-issue-card">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", gap: 10 }}>
        <div>
          <b>{i.title}</b>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginTop: 5, fontSize: 12, color: "var(--muted)" }}>
            {i.source === "report" ? <span className="pill-tag" style={{ background: "#f3e4cf", color: "#8a5a1f" }}>From inspection</span> : <span className="pill-tag" style={{ background: "#e4e9f0", color: "#3a5170" }}>{i.source === "owner" ? "Owner raised" : "Staff raised"}</span>}
            {i.report_title && <span>· {i.report_title}</span>}
            {i.created_at && <span>· {fmtD(i.created_at)}</span>}
            {i.created_by && <span>· {i.created_by}</span>}
          </div>
        </div>
        <StatusPill status={i.status} />
      </div>
      <p style={{ color: "var(--muted)", fontSize: 14, margin: "8px 0 12px" }}>{i.description}</p>
      {i.contractor_id && <div style={{ fontSize: 12, color: "var(--honey)", marginBottom: 8 }}>Assigned to a contractor{i.job_id ? " · job created" : ""}</div>}
      <div style={{ display: "grid", gap: 10, maxWidth: 580 }}>
        <textarea className="inp" rows={2} placeholder="Update / quote note to owner" value={note} onChange={(e) => setNote(e.target.value)} />
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
          <input className="inp" style={{ maxWidth: 120 }} type="number" placeholder="Quote €" value={quote} onChange={(e) => setQuote(e.target.value)} />
          <select className="inp" style={{ maxWidth: 130 }} value={i.priority || "Normal"} onChange={(e) => update(i.issue_id, { priority: e.target.value })} data-testid="issue-priority">{["Low", "Normal", "High", "Urgent"].map((s) => <option key={s}>{s}</option>)}</select>
          <select className="inp" style={{ maxWidth: 140 }} value={i.status} onChange={(e) => update(i.issue_id, { status: e.target.value })}>{["open", "in progress", "resolved"].map((s) => <option key={s}>{s}</option>)}</select>
          <button className="btn-ink" onClick={() => update(i.issue_id, { staff_note: note, quote_amount: quote ? parseFloat(quote) : null })}>Save</button>
          {i.status !== "resolved" && <button className="btn-outline" data-testid="resolve-issue-btn" onClick={() => update(i.issue_id, { status: "resolved" })}>Mark resolved</button>}
        </div>
        {i.approval && <div><span className="lbl-m">Owner: </span><StatusPill status={i.approval} /></div>}
        {i.status === "resolved" && i.resolved_by && <div style={{ fontSize: 12, color: "#5B6440" }}>Resolved by {i.resolved_by}{i.resolved_at ? ` · ${fmtD(i.resolved_at)}` : ""}</div>}
      </div>
    </Card>
  );
}

export function MessagesTab({ pid, messages, reload }) {
  const [text, setText] = useState("");
  useEffect(() => { if (pid) api.post("/messages/read", { property_id: pid }).then(reload).catch(() => {}); }, [pid]); // eslint-disable-line
  const send = async (e) => { e.preventDefault(); if (!text.trim() || !pid) return;
    try { await api.post("/messages", { property_id: pid, body: text }); setText(""); reload(); } catch (e) { toast.error(apiErr(e)); } };
  const fmtMsg = (d) => { const dt = new Date(d); return isNaN(dt.getTime()) ? "" : dt.toLocaleString("en-GB", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" }); };
  return (
    <div style={{ maxWidth: 720 }}>
      <div style={{ display: "grid", gap: 14, marginBottom: 20 }}>
        {messages.map((m) => {
          const mine = m.sender_role !== "client";
          return (
            <div key={m.message_id} style={{ display: "flex", flexDirection: "column", alignItems: mine ? "flex-end" : "flex-start" }}>
              <div style={{ padding: "11px 15px", borderRadius: mine ? "14px 14px 4px 14px" : "14px 14px 14px 4px", background: mine ? "var(--ink)" : "var(--soft)", color: mine ? "var(--paper)" : "var(--ink)", border: mine ? "none" : "1px solid var(--line)", maxWidth: "80%", fontSize: 14, lineHeight: 1.5 }}>{m.body}</div>
              <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 4 }}>{m.sender_name} · {fmtMsg(m.created_at)}</div>
            </div>
          );
        })}
        {messages.length === 0 && <p style={{ color: "var(--muted)" }}>No messages yet.</p>}
      </div>
      <form onSubmit={send} style={{ display: "flex", gap: 10 }}>
        <input className="inp" placeholder="Reply to owner…" value={text} onChange={(e) => setText(e.target.value)} data-testid="staff-message-input" />
        <button className="btn-ink" type="submit">Send</button>
      </form>
    </div>
  );
}
